import Helper from './helper';
import moment from 'moment';
import { Directus, AxiosTransport, LocalStorage, MemoryStorage  } from '@directus/sdk';
import { ExtensionEndpoint } from '@directus/sdk/dist/handlers/extensions';
import { Auth } from './auth';
const axios = require('axios').default;

const isBrowser = typeof window !== 'undefined';

// Storage adapter where authentication state (token & expiration) is stored.
const storage = isBrowser ? new LocalStorage() : new MemoryStorage();

// Transport used to communicate with the server.
const transport = new AxiosTransport(process.env.GATSBY_API_URL, storage, async () => {
	await auth.refresh(); // This is how axios checks for refresh
});

// Auth is how authentication is handled, stored, and refreshed.
const auth = new Auth(transport, storage, {
	mode: 'json' //isBrowser ? 'cookie' : 'json',
});

const directus = new Directus(process.env.GATSBY_API_URL, {
	auth,
	storage,
	transport,
});

class Api {

  static authenticate(credential) {
    return directus.auth.login(credential);
  }

  static logout() {
    return directus.auth.logout();
  }

  static verifyPin(credential) {
    const ext = new ExtensionEndpoint(directus.transport, 'auth');
    return ext.post('/pos', credential, {refreshTokenIfNeeded: false, sendAuthorizationHeaders: false});
  }

  static customerGet(key) {
    const ext = new ExtensionEndpoint(directus.transport, 'pos');
    return ext.get('/customer/' + key);
  }

  static customerOrderByDate(owner, date) {
    const ext = new ExtensionEndpoint(directus.transport, 'pos');
    return ext.get(`/ordersbydate?date=${moment(date).format('yyyy-MM-DD')}&owner=${owner}`);
  }

  static loginGetList(counterType) {
    const ext = new ExtensionEndpoint(directus.transport, 'pos');
    // Set refreshTokenIfNeeded to false because this method just return a list of available logins
    // when the user is not login yet. If didn't set refreshTokenIfNeeded to false, the DirectUs SDK
    // will refresh the token if exist and expired. This might caused issue if the refresh token
    // is no login valid and still in the application storage. If didn't set to false, then the SDK
    // try to refresh the token but failed, the SDK will not proceed to invoke the /users endpoint
    // and return exception.
    return ext.get('/users/' + counterType, {refreshTokenIfNeeded: false, sendAuthorizationHeaders: false});
  }

  static supervisorGetList() {
    const ext = new ExtensionEndpoint(directus.transport, 'pos');
    return ext.get('/supervisor');
  }

  static allergenGetList() {
    return directus.items('list').readMany({
      filter: {
        "lang": { "_eq": "en-US" },
        "category": {"_eq":"ALLERGENS"},
        "status": {"_eq": "ACTIVE"}
      },
      sort: "text"
    })
  }

  static mealPeriod() {
    const ext = new ExtensionEndpoint(directus.transport, 'outlet');
    return ext.get('/collectiontime');
  }

  static mealMenuList(startDate, endDate) {
    const ext = new ExtensionEndpoint(directus.transport, 'meal');
    return ext.get(`/menu/?start=${moment(startDate).format('yyyy-MM-DD')}&end=${moment(endDate).format('yyyy-MM-DD')}`);
  }

  static mealDetail(mealId) {
    const ext = new ExtensionEndpoint(directus.transport, 'meal');
    return ext.get('/menu/' + mealId);
  }

  static mealItemStatus(itemId, type='meal') {
    const ext = new ExtensionEndpoint(directus.transport, 'meal');
    return ext.get(`/menuitemstatus/${itemId}?type=${type}`);
  }

  static mealSoldOut(items) {
    const ext = new ExtensionEndpoint(directus.transport, 'meal');
    return ext.post(`/menusoldout`, items);
  }

  static posHeartBeat(machineInfo, isLogin=false) {
    const ext = new ExtensionEndpoint(directus.transport, 'pos');
    return ext.post('/heartbeat', machineInfo, {refreshTokenIfNeeded: isLogin, sendAuthorizationHeaders: isLogin});
  }

  static checkHttpResponse(response) {
    if(response !== null && response !== undefined) {
      if(response.ok) {
        return response.json();
      }
      else {
        return {
          success: false,
          statusCode: 'HTTP_' + response.status,
          message: Helper.isNotNullAndUndefined(response.statusText) ? response.statusText : "Server error: HTTP " + response.status,
          payload: null
        }
      }
    }
    else {
      return {
        success: false,
        statusCode: 'HTTP_NORESPONSE',
        message: 'No response from the request.',
        payload: null
      }
    }
  }

  static posGetMachineInfo() {
    return new Promise((resolve, reject) => {
      fetch(process.env.CARDAGENT_API_URL + '/api/v1/getmachineinfo', {
        crossDomain:true,
        method: 'GET'
      })
      .then((response) => this.checkHttpResponse(response))
      .then(responseJson => {
        resolve(responseJson);
      }).catch(
        err => {
          reject(err);
      });
    });
  }

  static posReadCard() {
    return new Promise((resolve, reject) => {
      fetch(process.env.CARDAGENT_API_URL + '/api/v1/getcanid', {
        crossDomain:true,
        method: 'GET'
      })
      .then((response) => this.checkHttpResponse(response))
      .then(responseJson => {
        resolve(responseJson);
      }).catch(
        err => {
          reject(err);
      });
    });
  }

  static posQuitApp() {
    return new Promise((resolve, reject) => {
      fetch(process.env.CARDAGENT_API_URL + '/api/v1/quitapp', {
        crossDomain:true,
        method: 'GET'
      })
      .then((response) => this.checkHttpResponse(response))
      .then(responseJson => {
        resolve(responseJson);
      }).catch(
        err => {
          reject(err);
      });
    });
  }

  static orderCalculatePrice(order) {
    const ext = new ExtensionEndpoint(directus.transport, 'order');
    return ext.post(`/calculate`, order);
  }

  static orderSubmit(order) {
    const ext = new ExtensionEndpoint(directus.transport, 'order');
    return ext.post(`/new`, order);
  }

  static posOrder(date) {
    const ext = new ExtensionEndpoint(directus.transport, 'pos');
    return ext.get(`/posorders?date=${moment(date).format('yyyy-MM-DD')}`);
  }

  static posMealCount(counterType, mealPeriod, setName) {
    const ext = new ExtensionEndpoint(directus.transport, 'pos');
    return ext.get(`/mealcount?counter=${counterType}&period=${mealPeriod}&set=${setName}`);
  }

  static voidOrder(id) {
    const ext = new ExtensionEndpoint(directus.transport, 'order');
    return ext.get(`/void/${id}`);
  }

  static posGetSlide() {
    const ext = new ExtensionEndpoint(directus.transport, 'pos');
    // Set refreshTokenIfNeeded to false because this method just return a list of available slides
    // even the user is not login yet. If didn't set refreshTokenIfNeeded to false, the DirectUs SDK
    // will refresh the token if exist and expired. This might caused issue if the refresh token
    // is no login valid and still in the application storage. If didn't set to false, then the SDK
    // try to refresh the token but failed, the SDK will not proceed to invoke the /users endpoint
    // and return exception.
    return ext.get('/slide', {refreshTokenIfNeeded: false, sendAuthorizationHeaders: false});
  }

  static posGetServedCustomer() {
    const ext = new ExtensionEndpoint(directus.transport, 'pos');
    return ext.get(`/servedcust`);
  }

  static vendingMachineGetList() {
    const ext = new ExtensionEndpoint(directus.transport, 'vending');
    return ext.get(`/machines`);
  }

  // This api call the vending machine api directly, not via directus api. (old vending machine)
  // static vendingBoxesGetInfo(machineUrl, machineId) {
  //   return new Promise((resolve, reject) => {
  //     if(machineUrl[machineUrl.length - 1] !== '/')
  //       machineUrl += '/'; // Append the / if it is not there

  //     fetch(machineUrl + 'locker/getinfo', {
  //       crossDomain:true,
  //       method: 'GET',
  //       headers: {
  //         'MachineId' : machineId
  //       }
  //     })
  //     .then((response) => this.checkHttpResponse(response))
  //     .then(responseJson => {
  //       resolve(responseJson);
  //     }).catch(
  //       err => {
  //         reject(err);
  //     });
  //   });
  // }

  static vendingBoxesGetInfo(machineId) {
    const ext = new ExtensionEndpoint(directus.transport, 'vending');
    return ext.get(`/machines/boxlist/${machineId}`);
  }
  
  // This api call the vending machine api directly, not via directus api.
  // static vendingBoxesOpen(machineUrl, machineId, boxes) {
  //   return new Promise((resolve, reject) => {
  //     if(machineUrl[machineUrl.length - 1] !== '/')
  //       machineUrl += '/'; // Append the / if it is not there

  //     fetch(machineUrl + 'locker/unlock', {
  //       crossDomain:true,
  //       method: 'POST',
  //       headers: {
  //         'MachineId' : machineId,
  //         'Content-Type': 'application/json'
  //       },
  //       body: JSON.stringify({
  //         lockers: boxes
  //       })
  //     })
  //     .then((response) => this.checkHttpResponse(response))
  //     .then(responseJson => {
  //       resolve(responseJson);
  //     }).catch(
  //       err => {
  //         reject(err);
  //     });
  //   });
  // }

  static vendingBoxesOpen(machineId, boxes) {
    const ext = new ExtensionEndpoint(directus.transport, 'vending');
    return ext.post(`/machines/boxopen/${machineId}`, boxes);
  }

  static vendingBoxesBook(machineId, boxes) {
    const ext = new ExtensionEndpoint(directus.transport, 'vending');
    return ext.post(`/machines/boxbook/${machineId}`, boxes);
  }

  static vendingBoxesCancelBook(machineId, orderCodes) {
    const ext = new ExtensionEndpoint(directus.transport, 'vending');
    return ext.post(`/machines/boxcancelbook/${machineId}`, orderCodes);
  }

  static vendingBoxesResetBook() {
    const ext = new ExtensionEndpoint(directus.transport, 'vending');
    return ext.delete(`/machines/boxresetbookpreorder`);
  }

  static vendingBoxesPeriodCloseReset() {
    const ext = new ExtensionEndpoint(directus.transport, 'vending');
    return ext.delete(`/machines/boxpreiodclosereset`);
  }

  // This api call the vending machine api directly, not via directus api.
  // static vendingBoxesSetStatus(machineUrl, machineId, boxes) {
  //   return new Promise((resolve, reject) => {
  //     if(machineUrl[machineUrl.length - 1] !== '/')
  //       machineUrl += '/'; // Append the / if it is not there

  //     let localBoxes = Helper.deepCopy(boxes);
  //     localBoxes = localBoxes.sort((a, b) => a.status > b.status ? 1 : -1);
  //     let data = [];
  //     let dataIdx = 0;
  //     let lastStatus = null;
  //     for(let idx=0; idx<localBoxes.length; idx++) {
  //       if(lastStatus === null || lastStatus !== localBoxes[idx].status) {
  //         data.push({
  //           status: localBoxes[idx].status,
  //           lockers: []
  //         });

  //         if(lastStatus !== null)
  //           dataIdx++;

  //         lastStatus = localBoxes[idx].status;
  //       }

  //       if(data.length > 0 && data.length > dataIdx)
  //         data[dataIdx].lockers.push(localBoxes[idx].locker);
  //     }

  //     fetch(machineUrl + 'locker/status', {
  //       crossDomain:true,
  //       method: 'POST',
  //       headers: {
  //         'MachineId' : machineId,
  //         'Content-Type': 'application/json'
  //       },
  //       body: JSON.stringify(data)
  //     })
  //     .then((response) => this.checkHttpResponse(response))
  //     .then(responseJson => {
  //       resolve(responseJson);
  //     }).catch(
  //       err => {
  //         reject(err);
  //     });
  //   });
  // }

  // This api call the vending machine api directly, not via directus api.
  // The boxes is an array of the following objects
  // {
  //   passCode: "xxxxxx",
  //   passCodeType: 2, // 0 = not set, 1 = pin, 2 = QR or NCF Card ID
  //   locker: 101
  // }
  // static vendingBoxesSetPassCode(machineUrl, machineId, boxes) {
  //   return new Promise((resolve, reject) => {
  //     if(machineUrl[machineUrl.length - 1] !== '/')
  //       machineUrl += '/'; // Append the / if it is not there

  //     fetch(machineUrl + 'locker/passcode', {
  //       crossDomain:true,
  //       method: 'POST',
  //       headers: {
  //         'MachineId' : machineId,
  //         'Content-Type': 'application/json'
  //       },
  //       body: JSON.stringify(boxes.map(item => {
  //         return {
  //           passCode: item.passCode,
  //           passCodeType: item.passCodeType,
  //           lockers: [item.locker]
  //         }
  //       }))
  //     })
  //     .then((response) => this.checkHttpResponse(response))
  //     .then(responseJson => {
  //       resolve(responseJson);
  //     }).catch(
  //       err => {
  //         reject(err);
  //     });
  //   });
  // }

  static vendingCallInItems(mealPeriod, mealDate, availBoxes) {
    const ext = new ExtensionEndpoint(directus.transport, 'vending');
    return ext.post(`/callin?date=${moment(mealDate).format('yyyy-MM-DD')}&period=${mealPeriod}`, availBoxes);
  }

  static vendingMealPeriodClose() {
    const ext = new ExtensionEndpoint(directus.transport, 'vending');
    return ext.get(`/periodclose`);
  }

  static vendingUpdateAllocation(machineRecId, allocation) {
    const ext = new ExtensionEndpoint(directus.transport, 'vending');
    return ext.post(`/updateallocation?machineRecId=${machineRecId}`, allocation);
  }

  static retailMenuItems(selectedDate, mealCat) {
    const ext = new ExtensionEndpoint(directus.transport, 'meal');
    return ext.get(`/retailmenuitems/?selectedDate=${moment(selectedDate).format('yyyy-MM-DD')}&mealCat=${mealCat}`);
  }

  static addRetailItemToBox(machineRecId, boxId, retailItemId, retailPinCode, retailOrderCode) {
    const ext = new ExtensionEndpoint(directus.transport, 'vending');
    return ext.post(`/addretailitemtobox`, {machineRecId, boxId, retailItemId, retailPinCode, retailOrderCode});
  }

  static removeRetailItem(machineRecId, boxId) {
    const ext = new ExtensionEndpoint(directus.transport, 'vending');
    return ext.delete(`/deleteretailitem`, {machineRecId, boxId});
  }

  static webPushReg(regInfo) {
    const ext = new ExtensionEndpoint(directus.transport, 'webpush');
    return ext.post('/reg', regInfo, {refreshTokenIfNeeded: false, sendAuthorizationHeaders: false});
  }

  static webPushNotify() {
    const ext = new ExtensionEndpoint(directus.transport, 'webpush');
    return ext.get('/notify', {refreshTokenIfNeeded: false, sendAuthorizationHeaders: false});
  }
}

export default Api;